import { Workspace } from '@botpress/client'
import { Button, Popover, PopoverContent, PopoverTrigger, showDialog } from '@bpinternal/ui-kit-next'
import { Bell, InboxContent } from '@novu/react'
import { BellIcon, Headset } from 'lucide-react'
import { HelpDeskUpgradeDialog } from '~/components/HelpDeskUpgradeDialog'
import { AnnouncementsButton } from '~/features/announcements/components/AnnouncementButton'
import { useCurrentUser, useFeature, useWorkspaces } from '~/hooks'
import { INTERCOM_ELEMENT_ID } from '~/providers/IntercomProvider'
import { NavUser } from './NavUser'
import { Boundary } from '../Boundary'

interface HeaderSettingsProps {
  workspaces: Workspace[]
  workspaceId?: string
}

export function HeaderSettings({ workspaces, workspaceId }: HeaderSettingsProps) {
  return (
    <div className="flex max-w-full items-center gap-1">
      {workspaceId && <AnnouncementsButton workspaceId={workspaceId} />}
      {workspaceId && <HelpDeskButton workspaceId={workspaceId} />}
      <NotificationsButton />
      <NavUser workspaces={workspaces} />
    </div>
  )
}

function HelpDeskButton({ workspaceId }: { workspaceId: string }) {
  const activeWorkspace = useWorkspaces().data?.find((w) => w.id === workspaceId)
  const plan = activeWorkspace?.plan
  const helpDeskEnabled = plan && plan !== 'community'

  const webchatInsteadOfIntercom = useFeature('webchat-instead-of-intercom')
  const user = useCurrentUser()

  if (helpDeskEnabled) {
    return (
      <Button
        variant="ghost"
        size="icon"
        className="h-7 w-7 sm:h-8 sm:w-8"
        id={webchatInsteadOfIntercom ? '' : INTERCOM_ELEMENT_ID}
        onClick={() => {
          if (window.botpress) {
            window.botpress.toggle()
            window.botpress.updateUser({
              name: user.displayName,
              data: {
                name: user.displayName,
                email: user.email,
                userId: user.id,
                workspaceId: activeWorkspace?.id,
              },
            })
          }
        }}
      >
        <Headset className="!h-4 !w-4 sm:!h-5 sm:!w-5" />
      </Button>
    )
  }

  return (
    <Button
      variant="ghost"
      size="icon"
      className="h-7 w-7 sm:h-8 sm:w-8"
      onClick={() =>
        showDialog(({ close }) => <HelpDeskUpgradeDialog close={close} workspaceId={workspaceId} />, {
          title: 'Help Desk Locked',
        })
      }
    >
      <Headset className="!h-4 !w-4 sm:!h-5 sm:!w-5" />
    </Button>
  )
}

function NotificationsButton() {
  return (
    <Popover>
      <PopoverTrigger>
        <Bell
          renderBell={(unreadCount) => (
            <Button variant="ghost" className=" relative mr-1 h-7 w-7 sm:mr-2 sm:h-8 sm:w-8">
              <BellIcon className="!h-4 !w-4 text-foreground sm:!h-5 sm:!w-5" />
              {unreadCount > 0 && (
                <div className="absolute right-0.5 top-0.5 size-1.5 rounded-full bg-red-500 sm:size-2"></div>
              )}
            </Button>
          )}
        />
      </PopoverTrigger>
      <PopoverContent collisionPadding={20} className="h-[400px] w-[320px] p-0 sm:h-[600px] sm:w-[450px]">
        <InboxContent />
      </PopoverContent>
    </Popover>
  )
}
